import { PLAYWRIGHT_E2E_COOKIE } from '@lib/analytics/constants';
import { medusaSdkFingerprint } from '@lib/util/sentry-fingerprint';
import type * as Sentry from '@sentry/nextjs';

export const BROWSER_INJECTED_PREFIXES = [
  'app:///',
  'safari-extension://',
  'safari-web-extension://',
  'chrome-extension://',
  'moz-extension://',
];

export function isBrowserInjectedFilename(filename: string): boolean {
  return BROWSER_INJECTED_PREFIXES.some((prefix) =>
    filename.startsWith(prefix),
  );
}

type BeforeSend = NonNullable<
  NonNullable<Parameters<typeof Sentry.init>[0]>['beforeSend']
>;

/**
 * Returns a Sentry `beforeSend` callback for the client-side SDK.
 *
 * Extracted so the filtering logic can be unit-tested without a real browser.
 *
 * @param getCookies - returns the current `document.cookie` string
 * @param getHasConsent - returns true if analytics consent has been granted
 */
export function makeClientBeforeSend(
  getCookies: () => string,
  getHasConsent: () => boolean,
): BeforeSend {
  const e2eCookieRe = new RegExp(`(?:^|;\\s*)${PLAYWRIGHT_E2E_COOKIE}=`);
  return (event, hint) => {
    if (e2eCookieRe.test(getCookies())) {
      return null;
    }

    // Drop synthetic CustomEvent unhandled rejections from third-party SDKs (PostHog).
    // These occur when a Promise is rejected with a CustomEvent object — Sentry cannot
    // extract a message, so every captured event is noise with no stack trace.
    const values = event.exception?.values;
    if (
      values &&
      values.length > 0 &&
      values.every(
        (v) => v.type === 'CustomEvent' && v.mechanism?.synthetic === true,
      )
    ) {
      return null;
    }

    const frames = event.exception?.values?.flatMap(
      (v) => v.stacktrace?.frames ?? [],
    );
    if (frames?.some((f) => isBrowserInjectedFilename(f.filename ?? ''))) {
      return null;
    }

    if (!getHasConsent()) {
      delete event.user;

      if (event.request) {
        delete event.request.cookies;
        delete event.request.headers;
      }
    }

    const fingerprint = medusaSdkFingerprint(hint?.originalException);
    if (fingerprint) {
      event.fingerprint = fingerprint;
    }

    return event;
  };
}
